import AsyncStorage from '@react-native-async-storage/async-storage';
import { API_BASE_URL } from '../config/api';

class NotificationAPI {
  constructor() {
    this.baseURL = `${API_BASE_URL}/api/notifications`;
  }

  async getAuthHeaders() {
    const sessionId = (await AsyncStorage.getItem('session_id')) || '';
    const rememberToken = (await AsyncStorage.getItem('remember_token')) || '';

    return {
      'Content-Type': 'application/json',
      'X-Session-ID': sessionId,
      'X-Remember-Token': rememberToken,
    };
  }

  /**
   * Notification API returns { status, message, data } without `code`.
   * Do not use makeAuthenticatedApiCall — it requires status+code===200.
   */
  async request(path = '', options = {}) {
    const headers = {
      ...(await this.getAuthHeaders()),
      ...(options.headers || {}),
    };
    const response = await fetch(`${this.baseURL}${path}`, {
      ...options,
      headers,
    });
    const data = await response.json();
    if (data?.new_session_id) {
      await AsyncStorage.setItem('session_id', data.new_session_id);
    }
    return data;
  }

  async testConnection() {
    try {
      const response = await fetch(`${this.baseURL}/test`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      return await response.json();
    } catch (error) {
      console.error('Notification API test failed:', error);
      throw error;
    }
  }

  async getNotifications(page = 1, limit = 10, status = 'all', dateFrom = null, dateTo = null) {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        status,
      });

      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);

      return await this.request(`?${params.toString()}`, { method: 'GET' });
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
      throw error;
    }
  }

  /** Lightweight unread count for header badge. */
  async getUnreadCount() {
    try {
      const response = await this.getNotifications(1, 1, 'all');
      if (response?.status === 'success') {
        return Math.max(0, Number(response?.data?.summary?.unread_count) || 0);
      }
      return 0;
    } catch (error) {
      console.error('Failed to fetch unread notification count:', error);
      return 0;
    }
  }

  async markAsRead(notificationId) {
    try {
      return await this.request(`/${notificationId}/read`, { method: 'PUT' });
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
      throw error;
    }
  }

  async markAllAsRead() {
    try {
      return await this.request('/mark-all-read', { method: 'PUT' });
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
      throw error;
    }
  }

  async deleteNotification(notificationId) {
    try {
      return await this.request(`/${notificationId}`, { method: 'DELETE' });
    } catch (error) {
      console.error('Failed to delete notification:', error);
      throw error;
    }
  }

  formatDate(timestamp) {
    const date = new Date(timestamp * 1000);
    return date.toLocaleDateString('id-ID', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }
}

export default new NotificationAPI();
